import { useState, useCallback, useEffect, useMemo } from 'react';
import { useAtomValue } from 'jotai';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { GenericDialog } from '@/components/shared/GenericDialog';
import { abyssColors } from '@/theme/colors';
import { containedButtonStyle } from '@/theme';
import {
  useGetAllStructuresListForTemplateBuilderQuery,
  useAllLogsTemplatesByTypeQuery,
  LogTemplateType,
} from '@/__generated__/graphql';
import { selectedLogsTemplateType } from '../state';

type CopyTemplateDialogProps = {
  open: boolean;
  onClose: () => void;
  onCopy: (sourceStructureId: string, sourceTemplateId: string) => void;
  currentStructureId: string;
};

export const CopyTemplateDialog = ({
  open,
  onClose,
  onCopy,
  currentStructureId,
}: CopyTemplateDialogProps) => {
  const [selectedSourceStructureId, setSelectedSourceStructureId] = useState<string>('');
  const selectedTemplateType = useAtomValue(selectedLogsTemplateType);

  const { data: allStructures } = useGetAllStructuresListForTemplateBuilderQuery();

  const templateKindLabel = useMemo(() => {
    if (selectedTemplateType === LogTemplateType.QaqcTemplate) return 'QA/QC';
    if (selectedTemplateType === LogTemplateType.IndirectPobTemplate) return 'Indirect POB';
    if (selectedTemplateType === LogTemplateType.CostLogTemplate) return 'cost log';
    return 'worklogs';
  }, [selectedTemplateType]);

  const { data: allLogsTemplates } = useAllLogsTemplatesByTypeQuery({
    variables: {
      input: {
        structureId: selectedSourceStructureId,
        type: selectedTemplateType,
      },
    },
    skip: !selectedSourceStructureId,
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (!open) {
      setSelectedSourceStructureId('');
    }
  }, [open]);

  const handleStructureChange = useCallback((structureId: string) => {
    setSelectedSourceStructureId(structureId);
  }, []);

  const availableStructures =
    allStructures?.allStructures?.filter(
      (structure: { id: string }) => structure.id !== currentStructureId
    ) || [];

  const availableTemplates = useMemo(
    () => allLogsTemplates?.allLogsTemplates || [],
    [allLogsTemplates?.allLogsTemplates]
  );

  const handleCopy = useCallback(() => {
    const logsTemplate = availableTemplates[0];
    if (selectedSourceStructureId && logsTemplate?.id) {
      onCopy(selectedSourceStructureId, logsTemplate.id);
    }
  }, [selectedSourceStructureId, availableTemplates, onCopy]);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  const isValid = useMemo(
    () => selectedSourceStructureId && availableTemplates.length > 0,
    [selectedSourceStructureId, availableTemplates]
  );

  return (
    <GenericDialog
      open={open}
      onClose={onClose}
      title={`Copy ${templateKindLabel} template`}
      content={
        <Stack spacing={3} sx={{ minWidth: 400, pt: 2 }}>
          <Typography fontWeight={400} fontSize="14px" color={abyssColors.primary[500]}>
            {`Select a structure to copy the ${templateKindLabel} template from:`}
          </Typography>

          <Autocomplete
            fullWidth
            options={availableStructures}
            getOptionLabel={(option) => option.name}
            value={
              availableStructures.find(
                (structure: { id: string }) => structure.id === selectedSourceStructureId
              ) || null
            }
            onChange={(_, newValue) => {
              handleStructureChange(newValue?.id || '');
            }}
            renderInput={(params) => (
              <TextField {...params} label="Source Structure" placeholder="Search structures..." />
            )}
            noOptionsText="No structures available"
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />

          {selectedSourceStructureId && !availableTemplates.length && (
            <Typography
              variant="body2"
              color={abyssColors.primary[300]}
              sx={{ fontStyle: 'italic' }}
            >
              {`No ${templateKindLabel} templates found for the selected structure!`}
            </Typography>
          )}

          {selectedSourceStructureId && availableTemplates.length > 0 && (
            <Typography variant="body2" color={abyssColors.primary[500]} sx={{ fontWeight: 500 }}>
              {`Found ${templateKindLabel} template: ${availableTemplates[0]?.name ?? ''}`}
            </Typography>
          )}
        </Stack>
      }
      actions={
        <>
          <Button variant="text" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCopy}
            disabled={!isValid}
            sx={containedButtonStyle}
          >
            {`Copy ${templateKindLabel} template`}
          </Button>
        </>
      }
      actionsStyles={{ mr: 3, pb: 3 }}
    />
  );
};
